import styled, { css, keyframes } from 'styled-components';
import { px2rem, opacify, themeVariant } from '../utils/helpers/style.js';
import { styles } from '../constants/styles.js';
import { spacing } from '../constants/spacing.js';
import { fontFamilyStyles } from '../ContentText/styles.web.js';
import { lineHeights } from '../constants/line-heights.js';

var toggleDimensions = {
  fontSize: {
    normal: px2rem(18),
    condensed: px2rem(16)
  },
  minHeight: {
    normal: px2rem(26),
    condensed: px2rem(12)
  },
  height: {
    normal: px2rem(28),
    condensed: px2rem(18)
  },
  focusIndicator: {
    normal: {
      height: px2rem(40),
      width: px2rem(72),
      borderRadius: px2rem(20),
      centerOffset: px2rem(-4)
    },
    condensed: {
      height: px2rem(32),
      width: px2rem(56),
      borderRadius: px2rem(16),
      centerOffset: px2rem(-4)
    }
  },
  width: {
    normal: px2rem(60),
    condensed: px2rem(42)
  },
  paddingLeft: {
    normal: spacing.xxl,
    condensed: spacing.l
  },
  paddingTop: {
    normal: px2rem(6),
    condensed: 0
  },
  lineHeight: {
    normal: lineHeights.tight,
    condensed: lineHeights.normal
  }
};
var sliderVariants = {
  radius: {
    normal: px2rem(28),
    condensed: px2rem(18)
  },
  border: {
    normal: px2rem(2),
    condensed: px2rem(3)
  },
  left: {
    normal: px2rem(2),
    condensed: px2rem(3)
  },
  top: {
    normal: px2rem(2),
    condensed: px2rem(3)
  },
  checkedLeft: {
    normal: px2rem(34),
    condensed: px2rem(27)
  }
};
var focusIndicatorDimensions = {
  radius: {
    normal: px2rem(44),
    condensed: px2rem(40)
  },
  offsetLeft: {
    normal: px2rem(-6),
    condensed: px2rem(-8)
  },
  offsetTop: {
    normal: px2rem(-6),
    condensed: px2rem(-8)
  },
  checkedLeft: {
    normal: px2rem(26),
    condensed: px2rem(16)
  }
};
var focusIndicator = /*#__PURE__*/styled.span.withConfig({
  displayName: "styles__focusIndicator",
  componentId: "sc-88w41q-0"
})(["pointer-events:none;background:", ";opacity:0;&:before{box-sizing:content-box;background:", ";border-radius:", ";content:'';display:block;height:", ";left:", ";position:absolute;top:", ";transition:background ", ",border ", ";transition-timing-function:", ";width:", ";}"], function (p) {
  return opacify(p.$color, p.theme.focusIndicator.opacity);
}, function (p) {
  return opacify(p.$color, p.theme.focusIndicator.opacity);
}, function (p) {
  return toggleDimensions.focusIndicator[p.$variant].borderRadius;
}, function (p) {
  return toggleDimensions.focusIndicator[p.$variant].height;
}, function (p) {
  return toggleDimensions.focusIndicator[p.$variant].centerOffset;
}, function (p) {
  return toggleDimensions.focusIndicator[p.$variant].centerOffset;
}, styles.transition.timing.base, styles.transition.timing.base, styles.transition["function"].base, function (p) {
  return toggleDimensions.focusIndicator[p.$variant].width;
});
var focusAnimation = /*#__PURE__*/keyframes(["from{transform:scale(.1,.1);opacity:0;}to{transform:scale(1,1);opacity:1;}"]);
var slider = /*#__PURE__*/styled.span.withConfig({
  displayName: "styles__slider",
  componentId: "sc-88w41q-1"
})(["background:", ";border-radius:", ";height:", ";left:", ";pointer-events:none;position:absolute;top:", ";transition:left ", ";transition-timing-function:", ";width:", ";"], function (p) {
  return p.theme.toggle.web.slider.colors["default"];
}, themeVariant(sliderVariants.radius), themeVariant(sliderVariants.radius), themeVariant(sliderVariants.border), themeVariant(sliderVariants.top), styles.transition.timing.base, styles.transition["function"].base, themeVariant(sliderVariants.radius));
var textLabel = /*#__PURE__*/styled.span.withConfig({
  displayName: "styles__textLabel",
  componentId: "sc-88w41q-2"
})(["", " color:", ";cursor:pointer;display:flex;font-size:", ";line-height:", ";min-height:", ";padding-left:", ";padding-top:", ";user-select:none;&:empty{padding-left:", ";}&:before{box-sizing:content-box;border:", " solid ", ";background:", ";border-radius:", ";content:'';display:block;height:", ";left:0;position:absolute;top:0;transition:background ", ",border ", ";transition-timing-function:", ";width:", ";}"], fontFamilyStyles, function (p) {
  return p.theme.toggle.web.textColor;
}, themeVariant(toggleDimensions.fontSize), themeVariant(toggleDimensions.lineHeight), themeVariant(toggleDimensions.minHeight), themeVariant(toggleDimensions.paddingLeft), themeVariant(toggleDimensions.paddingTop), spacing.xl, themeVariant(sliderVariants.border), function (p) {
  return p.theme.toggle.web.background.inactive;
}, function (p) {
  return p.theme.toggle.web.background.inactive;
}, px2rem(16), themeVariant(toggleDimensions.height), styles.transition.timing.base, styles.transition.timing.base, styles.transition["function"].base, themeVariant(toggleDimensions.width));
var input = /*#__PURE__*/styled.input.withConfig({
  displayName: "styles__input",
  componentId: "sc-88w41q-3"
})(["opacity:0;position:absolute;&:focus-visible{~ ", "{outline-offset:0;box-shadow:0 0 0 4px ", ";outline:2px solid ", ";}}&:focus,&:hover{~ ", "{&:before{border-color:", ";}}}&:not(:disabled):hover{~ ", "{animation:", " ", " forwards;}}&:checked{~ ", "{&:before{border-color:", ";background:", ";}}~ ", "{left:", ";&:after{opacity:1;}}~ ", "{left:", ";}}", ""], textLabel, function (p) {
  return p.theme.tokens.focus.primary;
}, function (p) {
  return p.theme.tokens.focus.secondary;
}, textLabel, function (props) {
  return props.$color;
}, focusIndicator, focusAnimation, styles.transition.timing.base, textLabel, function (props) {
  return props.$color;
}, function (props) {
  return props.$color;
}, slider, themeVariant(sliderVariants.checkedLeft), focusIndicator, themeVariant(focusIndicatorDimensions.checkedLeft), function (props) {
  return props.$locked ? css(["~ ", "{cursor:not-allowed;}"], textLabel) : css(["&:disabled{~ ", "{color:", ";cursor:not-allowed;&:before{border-color:", ";background:", ";}}~ ", "{background:", ";&:after{border-color:", ";}}}"], textLabel, function (p) {
    return p.theme.toggle.web.background.disabled;
  }, function (p) {
    return p.theme.toggle.web.background.disabled;
  }, function (p) {
    return p.theme.toggle.web.background.disabled;
  }, slider, function (p) {
    return p.theme.toggle.web.slider.colors.disabled;
  }, function (p) {
    return p.theme.toggle.web.background.disabled;
  });
});

export { focusIndicator, input, slider, textLabel };
